import React, { useState } from 'react';
import { Card, Form, Button, Badge, Modal } from 'react-bootstrap';
import { Eye, EyeOff, RotateCcw, Plus, Pencil } from 'lucide-react';
import { getEventTypeLabel, getEventColor } from '../../types/timeline';
import { ReportDraftFieldId, ReportDraftItem, REPORT_DRAFT_FIELD_IDS } from '../../types/reportDrafts';
import { ReportDraftRenderEvent, ResolvedDraftPhoto } from '../../utils/reportDraftView';
import { useFirebaseImage, getImagePathWithSize, getFreshDownloadURLWithFallback, ImageSize } from '../../Helpers/ImageLoader';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

const FIELD_LABELS: { [field in ReportDraftFieldId]: string } = {
    description: 'Beskrivning',
    buildingName: 'Byggnad',
    completionComment: 'Slutkommentar',
    measurement: 'Mätvärde'
};

interface DraftPhotoThumbProps {
    photo: ResolvedDraftPhoto;
    onToggle: () => void;
}

const DraftPhotoThumb: React.FC<DraftPhotoThumbProps> = ({ photo, onToggle }) => {
    // Thumbnail variant for the card; the full-size image is fetched lazily on
    // click and shown in Fancybox (same pattern as ClickableImage).
    const { src, isLoading } = useFirebaseImage(getImagePathWithSize(photo.storagePath, ImageSize.THUMBNAIL_SMALL));

    const openFullSize = async () => {
        try {
            const fullUrl = await getFreshDownloadURLWithFallback(photo.storagePath);
            Fancybox.show(
                [{ src: fullUrl, caption: photo.caption || '' }],
                { animated: true, showClass: 'fancybox-fadeIn' }
            );
        } catch (error) {
            console.error('Failed to load full-size image:', photo.storagePath, error);
        }
    };

    return (
        <div className={`report-draft-photo ${photo.excluded ? 'report-draft-photo-excluded' : ''}`}>
            <div className="report-draft-photo-frame">
                {!isLoading && src && (
                    <img
                        src={src}
                        alt={photo.caption || 'Foto'}
                        role="button"
                        style={{ cursor: 'pointer' }}
                        onClick={openFullSize}
                    />
                )}
                <Form.Check
                    type="checkbox"
                    className="report-draft-photo-check"
                    title={photo.excluded ? 'Ta med fotot i rapporten' : 'Uteslut fotot ur rapporten'}
                    checked={!photo.excluded}
                    onChange={onToggle}
                />
            </div>
            {photo.caption && (
                <div className="small text-muted text-truncate" title={photo.caption}>{photo.caption}</div>
            )}
        </div>
    );
};

interface ReportDraftItemCardProps {
    rendered: ReportDraftRenderEvent;
    /** Persist a changed item. Typed edits pass debounce=true. */
    onChange: (item: ReportDraftItem, debounce: boolean) => void;
    onAddParagraphAfter: () => void;
}

/**
 * One member event in the draft editor. The card is a read-only preview of
 * what the PDF prints (snapshot lines, photos, comment); all text editing —
 * per-line text with hide/reset, photo captions and the comment — happens in
 * the pencil-opened edit modal. Everything edits the draft's own snapshot;
 * the timeline is never touched (plan D14).
 */
const ReportDraftItemCard: React.FC<ReportDraftItemCardProps> = ({
    rendered,
    onChange,
    onAddParagraphAfter
}) => {
    const item = rendered.item;
    const [showEditModal, setShowEditModal] = useState(false);

    const setField = (field: ReportDraftFieldId, change: Partial<{ text: string; hidden: boolean }>, debounce: boolean) => {
        const current = item.fields?.[field];
        if (!current) return;
        onChange({
            ...item,
            fields: { ...item.fields, [field]: { ...current, ...change } }
        }, debounce);
    };

    const togglePhoto = (photo: ResolvedDraftPhoto) => {
        const current = item.photos?.[photo.photoKey];
        if (!current) return;
        onChange({
            ...item,
            photos: { ...item.photos, [photo.photoKey]: { ...current, excluded: !current.excluded } }
        }, false);
    };

    const setCaption = (photo: ResolvedDraftPhoto, caption: string) => {
        const current = item.photos?.[photo.photoKey];
        if (!current) return;
        onChange({
            ...item,
            photos: { ...item.photos, [photo.photoKey]: { ...current, caption: caption || undefined } }
        }, true);
    };

    /** Fields present in this event's snapshot, in canonical order. */
    const presentFields = REPORT_DRAFT_FIELD_IDS.filter(field => item.fields?.[field] !== undefined);

    /** Read-only preview line on the card. */
    const previewLine = (field: ReportDraftFieldId) => {
        const value = item.fields![field]!;
        return (
            <div key={field} className="report-draft-field-row">
                <span className="report-draft-field-label small text-muted">{FIELD_LABELS[field]}</span>
                <span className={`small ${value.hidden ? 'report-draft-field-hidden text-muted' : ''}`}>
                    {value.hidden && <EyeOff size={12} className="me-1" />}
                    {value.text}
                </span>
            </div>
        );
    };

    /** Editable row inside the modal. */
    const editRow = (field: ReportDraftFieldId) => {
        const value = item.fields![field]!;
        return (
            <div key={field} className="report-draft-field-row">
                <span className="report-draft-field-label small text-muted">{FIELD_LABELS[field]}</span>
                <Form.Control
                    size="sm"
                    type="text"
                    value={value.text}
                    disabled={value.hidden}
                    className={value.hidden ? 'report-draft-field-hidden' : undefined}
                    onChange={(e) => setField(field, { text: e.target.value }, true)}
                />
                {value.text !== value.original && !value.hidden && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-1"
                        title="Återställ till originaltexten"
                        onClick={() => setField(field, { text: value.original }, false)}
                    >
                        <RotateCcw size={14} />
                    </Button>
                )}
                <Button
                    variant="link"
                    size="sm"
                    className="p-1"
                    title={value.hidden ? 'Visa raden i rapporten' : 'Dölj raden i rapporten'}
                    onClick={() => setField(field, { hidden: !value.hidden }, false)}
                >
                    {value.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                </Button>
            </div>
        );
    };

    const includedPhotos = rendered.photos.filter(p => !p.excluded);

    return (
        <Card className={`report-draft-item mb-2 ${rendered.included ? '' : 'report-draft-item-excluded'}`}>
            <Card.Body className="p-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                        <Badge bg="" style={{ backgroundColor: getEventColor(item.eventType) }}>
                            {getEventTypeLabel(item.eventType).toUpperCase()}
                        </Badge>
                        <span className="small text-muted">{item.date.split(' ')[0]}</span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            title="Redigera texter och kommentar"
                            onClick={() => setShowEditModal(true)}
                        >
                            <Pencil size={14} />
                        </Button>
                        <Form.Check
                            type="switch"
                            id={`include-${rendered.eventId}`}
                            label="Med i rapporten"
                            checked={rendered.included}
                            onChange={() => onChange({ ...item, included: !item.included }, false)}
                        />
                    </div>
                </div>

                {presentFields.map(previewLine)}

                {rendered.photos.length > 0 && (
                    <div className="report-draft-photos mt-2">
                        {rendered.photos.map(photo => (
                            <DraftPhotoThumb
                                key={photo.photoKey}
                                photo={photo}
                                onToggle={() => togglePhoto(photo)}
                            />
                        ))}
                    </div>
                )}

                {item.comment && item.comment.trim() && (
                    <div className="report-draft-comment small fst-italic text-muted mt-2">
                        {item.comment}
                    </div>
                )}

                <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1"
                    onClick={onAddParagraphAfter}
                >
                    <Plus size={14} className="me-1" />
                    Lägg till stycke efter händelsen
                </Button>
            </Card.Body>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Pencil size={18} className="me-2" style={{ verticalAlign: 'text-bottom' }} />
                        Redigera händelsen i rapporten
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted">
                        Ändringarna gäller bara detta utkast — händelsen i tidslinjen påverkas inte.
                    </p>

                    {presentFields.map(editRow)}

                    {includedPhotos.length > 0 && (
                        <Form.Group className="mt-3">
                            <Form.Label className="small text-muted mb-1">Bildtexter</Form.Label>
                            {includedPhotos.map(photo => (
                                <Form.Control
                                    key={photo.photoKey}
                                    size="sm"
                                    type="text"
                                    className="mb-1"
                                    value={photo.caption ?? ''}
                                    placeholder="Bildtext"
                                    onChange={(e) => setCaption(photo, e.target.value)}
                                />
                            ))}
                        </Form.Group>
                    )}

                    <Form.Group className="mt-3">
                        <Form.Label className="small text-muted mb-1">Kommentar (visas i rapporten)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={item.comment || ''}
                            placeholder="Skriv en kommentar till händelsen…"
                            onChange={(e) => onChange({ ...item, comment: e.target.value }, true)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowEditModal(false)}>
                        Klart
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
};

export default ReportDraftItemCard;
